import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Actions from 'flex/Actions';
import { createTimeSeries } from '../ResultAnswerUtils';
import { convertAdminQuestionnaire, convertQuestionnaire } from '../convertQuestionnaire';
import { useAppSelector } from 'flex/utils';

type Result = {
    isLoading: boolean,
    questionnaire: EverydayNote.Questionnaire | undefined
    timeSeries: EverydayNote.ResultTotal.TimeSeries
}

type TermInfo = {
    /** 基準となる日付 */
    base: number
    /** 期間の種類 */
    type: EverydayNote.ResultTotal.TermTypes
}

const INIT_STATE: Result = {
    isLoading: false,
    questionnaire: undefined,
    timeSeries: [],
};

export const useResultTotalQuestionnaire = () => {
    const [result, setResult] = useState<Result>({ ...INIT_STATE });
    const [term, setTerm] = useState<TermInfo>();
    const [question, setQuestion] = useState<EverydayNote.Question>();

    const isControllableUser = useAppSelector(state => state.isControllableUser.payload);
    const controlEverydayNoteResultTotalConnection = useAppSelector(state => state.controlEverydayNoteResultTotalConnection);
    const adminEverydayNoteControlResultTotalConnection = useAppSelector(state => state.adminEverydayNoteControlResultTotalConnection);
    const adminEverydayNoteResultTotalConnection = useAppSelector(state => state.adminEverydayNoteResultTotalConnection);

    const dispatch = useDispatch();

    /** hook の状態をすべてリセットする */
    const reset = useCallback(() => setResult({ ...INIT_STATE }), []);

    /** 通信を行う */
    const request = useCallback(
        (
            questionnaireUuid: string,
            question: EverydayNote.Question,
            data: Http.Connection.Request.Parameter.EverydayNote.ResultTotal,
            termType: EverydayNote.ResultTotal.TermTypes,
            tabName?: 'board' | 'school'
        ) => {

            const resultTotalAction = isControllableUser ?
                Actions.http.connection.everydayNote.control.resultTotal :
                tabName === 'board' ?
                    Actions.http.connection.everydayNote.admin.controlResultTotal :
                    Actions.http.connection.everydayNote.admin.resultTotal;

            dispatch(resultTotalAction(questionnaireUuid, question.uuid, data));
            setResult(state => ({ ...state, isLoading: true }));
            setTerm({
                base: data.start_at,
                type: termType,
            });
            setQuestion(question);
        },
        [dispatch]// eslint-disable-line
    );

    type Connections = EverydayNoteResultTotalConnection | EverydayNoteControlResultTotalConnection | AdminEverydayNoteResultTotalConnection;
    const connectionEffect = <T extends Connections>(connection: T, converter: (result: T['payload']['result']['note']) => EverydayNote.Questionnaire) => () => {
        const {
            meta: { status },
            payload: { result: payloadResult },
        } = connection;

        switch (status) {
            case Actions.statusEnum.REQUEST:
            case Actions.statusEnum.LOADING: {
                break;
            }
            case Actions.statusEnum.SUCCESS: {
                if (!result.isLoading) break;
                if (question === undefined) break;

                setResult({
                    isLoading: false,
                    questionnaire: converter(payloadResult.note),
                    timeSeries: createTimeSeries(payloadResult.items, question, term),
                });
                break;
            }
            case Actions.statusEnum.FAILURE:
            case Actions.statusEnum.ERROR: {
                setResult({ ...INIT_STATE });
                break;
            }
            default: {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _: never = status;
            }
        }
    };

    // カリー化の記述となっており、関数を指定するところに値が入っていると誤認？されてwarningとなっているため
    useEffect(// eslint-disable-line
        connectionEffect(controlEverydayNoteResultTotalConnection, convertQuestionnaire),
        [controlEverydayNoteResultTotalConnection]
    );
    useEffect(// eslint-disable-line
        connectionEffect(adminEverydayNoteControlResultTotalConnection, convertQuestionnaire),
        [adminEverydayNoteControlResultTotalConnection]
    );
    useEffect(// eslint-disable-line
        connectionEffect(adminEverydayNoteResultTotalConnection, convertAdminQuestionnaire),
        [adminEverydayNoteResultTotalConnection]
    );

    return [result, request, reset] as const;
};
